/**
 * The journal's wire surface — one row per call, acknowledged.
 *
 * Deliberately **not** routed through `api/persistence.ts`. That hook coalesces,
 * debounces and shows a failure without retrying, all of which are right for a
 * workspace and wrong for a journal: a fill entered once has no successor to
 * supersede it, so a write that fails leaves the record gone while the user
 * believes it saved. Reusing the hook is the easy move and it would look like it
 * worked.
 *
 * Every call therefore **throws** on failure rather than resolving quietly. The
 * caller is expected to surface it and offer the action again — a plan form that
 * clears itself on a failed save is the failure this file exists to prevent.
 *
 * Returns the stored row rather than void, because the server normalises: target
 * shares are rescaled to sum to one and a revision may be counted. A client that
 * kept its own copy would drift from what is actually saved.
 */

import { can } from "../lib/entitlements";
import { currentUserId, requestHeaders } from "../lib/user";
import type { Account, Execution, Plan, Trade, TradingJournal } from "@qufolio/model";
import { httpBase } from "./backend";

/** qv-data's base URL — the same one the workspace store uses. Empty disables. */
const BASE = httpBase(import.meta.env.VITE_QV_SET_URL as string | undefined);

/**
 * Whether the journal is available to this user. Off, the plan controls hide.
 *
 * Two reasons it can be off, and the call sites do not care which: there is no
 * server configured, or this account's plan does not include `feat:journal`.
 * Both mean "the controls must not be offered", and the existing switch already
 * says that — which is why the entitlement lands here rather than as a new
 * condition at each of the call sites.
 *
 * **Permissive until the answer arrives**, like everything in
 * `lib/entitlements.ts`: `can` is true while `/auth/me` is in flight, so the
 * plan controls do not appear and then vanish, which reads as the app taking a
 * feature away. The server refuses regardless — `/journal/*` carries
 * `require("feat:journal")` on the router.
 */
export function journalEnabled(): boolean {
  return BASE !== "" && can("feat:journal");
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  if (!BASE) throw new Error("no journal server configured");
  // Whose journal, threaded **here** rather than written into each path: the
  // server requires it on every endpoint, so a call site that forgot it would
  // 422 — but the one added next year is otherwise one omission away from
  // reading, or writing against, somebody else's positions.
  const separator = path.includes("?") ? "&" : "?";
  const url = `${BASE}${path}${separator}userId=${encodeURIComponent(currentUserId())}`;
  // `fetch` unbound from `window` on purpose — see `api/persistence.ts`, where
  // binding it to a captured `window` is what made the whole suite pass while
  // the app could not save at all.
  // The token, when there is one. Awaited per call rather than captured once:
  // the SDK refreshes an ID token an hour before it expires, so a copy held
  // here would eventually be the stale one — and a stale token is a 401 the
  // user cannot explain. Resolves to `{}` when nothing is configured, which is
  // what keeps the unauthenticated path byte-for-byte as it was.
  const auth = await requestHeaders();
  const response = await fetch(url, {
    ...init,
    // **After** `init`, not before: a caller that passes its own `headers`
    // would otherwise replace these wholesale and drop the token, which fails
    // as a 401 nowhere near the call that caused it.
    headers: { "content-type": "application/json", ...auth, ...(init?.headers ?? {}) },
  });
  if (!response.ok) {
    // The server answers a broken *rule* with 400 and a sentence — "a filled
    // plan's entry cannot change" — which is worth showing verbatim. Anything
    // else is an outage and says so.
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail ?? `journal ${response.status}`);
  }
  return (await response.json()) as T;
}

const post = <T>(path: string, body: unknown): Promise<T> =>
  call<T>(path, { method: "POST", body: JSON.stringify(body) });

/** The whole journal. Ship one has hundreds of rows, so there is no paging. */
export const fetchJournal = (): Promise<TradingJournal> => call<TradingJournal>("/journal");

/** The paper account, created on first ask — and the one call that mints a row. */
export const fetchAccount = (): Promise<Account> => call<Account>("/journal/account");

/**
 * Every plan on one symbol — what a chart asks for to draw its levels.
 *
 * By symbol rather than by id because the chart knows what it is showing and
 * nothing else. The levels are then **derived at render time**; see
 * `lib/plan-levels.ts` for why they are not stored annotations.
 */
export const fetchPlansFor = (symbol: string): Promise<Plan[]> =>
  call<Plan[]>(`/journal/plans/${encodeURIComponent(symbol)}`);

export const savePlan = (plan: Partial<Plan>): Promise<Plan> => post<Plan>("/journal/plan", plan);

export const deletePlan = (id: string): Promise<unknown> =>
  call(`/journal/plan/${encodeURIComponent(id)}`, { method: "DELETE" });

/**
 * Move a stop or a target after the freeze, and record that it moved.
 *
 * `source` is omitted here and defaults to `user` server-side: the client has no
 * business claiming a change came from a rule, and attribution is the whole
 * reason amendments exist.
 */
export const amendPlan = (
  id: string,
  field: "stop" | "target",
  to: number,
  targetId?: string,
  note?: string,
): Promise<Plan> =>
  post<Plan>(`/journal/plan/${encodeURIComponent(id)}/amend`, { field, to, targetId, note });

export const openTrade = (trade: Partial<Trade>): Promise<Trade> =>
  post<Trade>("/journal/trade", trade);

export const closeTrade = (id: string, closedAt?: string): Promise<Trade> =>
  post<Trade>(`/journal/trade/${encodeURIComponent(id)}/close`, { closedAt });

/** One fill. The first against a plan is what freezes its levels. */
export const recordExecution = (execution: Partial<Execution>): Promise<Execution> =>
  post<Execution>("/journal/execution", execution);
